import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { UserContext } from '../context/User';
import { ThemeContext } from '../context/Theme';
import {
  API,
  readStoredStatus,
  showError,
  showInfo,
  showSuccess,
} from '../helpers';
import Turnstile from 'react-turnstile';
import { User, Lock, Github, Sun, Moon } from 'lucide-react';
import Button from './ui/Button';
import Input from './ui/Input';
import Card from './ui/Card';

const LoginForm = () => {
  const [inputs, setInputs] = useState({
    username: '',
    password: '',
    totp_code: '',
  });
  const [searchParams] = useSearchParams();
  // eslint-disable-next-line
  const [submitted, setSubmitted] = useState(false);
  const { username, password, totp_code } = inputs;
  // eslint-disable-next-line
  const [userState, userDispatch] = useContext(UserContext);
  const [themeState, themeDispatch] = useContext(ThemeContext);
  let navigate = useNavigate();

  const [status, setStatus] = useState({});
  const [turnstileEnabled, setTurnstileEnabled] = useState(false);
  const [turnstileSiteKey, setTurnstileSiteKey] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');

  useEffect(() => {
    if (searchParams.get('expired')) {
      showError('未登录或登录已过期，请重新登录！');
    }
    let status = readStoredStatus();
    if (status) {
      setStatus(status);
      if (status.turnstile_check) {
        setTurnstileEnabled(true);
        setTurnstileSiteKey(status.turnstile_site_key);
      }
    }
  }, [searchParams]);

  const toggleTheme = () => {
    themeDispatch({ type: 'toggle' });
  };

  const onGitHubOAuthClicked = () => {
    window.open(
      `https://github.com/login/oauth/authorize?client_id=${status.github_client_id}&scope=user:email`
    );
  };

  function handleChange(e) {
    const { name, value } = e.target;
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitted(true);
    if (turnstileEnabled && turnstileToken === '') {
      showInfo('请稍后几秒重试，Turnstile 正在检查用户环境！');
      return;
    }
    if (username && password) {
      const res = await API.post(
        `/api/user/login?turnstile=${encodeURIComponent(turnstileToken)}`,
        {
          username,
          password,
          totp_code,
        }
      );
      const { success, message, data } = res.data;
      if (success) {
        userDispatch({ type: 'login', payload: data });
        localStorage.setItem('user', JSON.stringify(data));
        navigate('/');
        showSuccess('登录成功！');
      } else {
        showError(message);
      }
    }
  }

  return (
    <div
      className='flex h-screen items-center justify-center bg-gray-50 scale-100'
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--bg-secondary)',
      }}
    >
      <button
        onClick={toggleTheme}
        aria-label='切换主题'
        style={{
          position: 'fixed',
          top: '1rem',
          right: '1rem',
          width: '2.5rem',
          height: '2.5rem',
          borderRadius: '9999px',
          border: '1px solid var(--border-color)',
          backgroundColor: 'var(--bg-primary)',
          color: 'var(--text-secondary)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: 'var(--shadow-sm)',
          zIndex: 10,
        }}
      >
        {themeState.theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
      </button>
      <div style={{ width: '100%', maxWidth: '28rem' }}>
        <div
          className='text-center mb-8'
          style={{ textAlign: 'center', marginBottom: '2rem' }}
        >
          <img
            src='/logo.png'
            alt='Logo'
            style={{ height: '4rem', margin: '0 auto 1rem' }}
          />
          <h2
            style={{
              fontSize: '1.875rem',
              fontWeight: '800',
              color: 'var(--text-primary)',
            }}
          >
            NewAPI 网关
          </h2>
          <p style={{ marginTop: '0.5rem', color: 'var(--text-secondary)' }}>
            欢迎回来，请登录您的账户
          </p>
        </div>

        <Card padding='2rem' className='shadow-xl'>
          <form onSubmit={handleSubmit}>
            <Input
              label='用户名'
              icon={User}
              placeholder='请输入用户名'
              name='username'
              value={username}
              onChange={handleChange}
            />
            <Input
              label='密码'
              icon={Lock}
              type='password'
              placeholder='请输入密码'
              name='password'
              value={password}
              onChange={handleChange}
            />
            <Input
              label='两步验证码'
              icon={Lock}
              placeholder='如果已启用 2FA，请输入 6 位验证码'
              name='totp_code'
              value={totp_code}
              onChange={handleChange}
            />
            {turnstileEnabled && (
              <div style={{ margin: '1rem 0' }}>
                <Turnstile
                  sitekey={turnstileSiteKey}
                  theme={themeState.theme}
                  onVerify={(token) => setTurnstileToken(token)}
                  onExpire={() => setTurnstileToken('')}
                />
              </div>
            )}

            <Button
              variant='primary'
              className='w-full mt-4'
              style={{ width: '100%', marginTop: '1rem' }}
              type='submit'
              size='lg'
            >
              登录
            </Button>
          </form>

          <div
            style={{
              marginTop: '1.5rem',
              textAlign: 'center',
              fontSize: '0.875rem',
              color: 'var(--text-secondary)',
            }}
          >
            <div
              className='flex justify-between'
              style={{ display: 'flex', justifyContent: 'space-between' }}
            >
              <Link to='/reset' style={{ color: 'var(--primary-600)' }}>
                忘记密码？
              </Link>
              <Link to='/register' style={{ color: 'var(--primary-600)' }}>
                注册账户
              </Link>
            </div>
          </div>

          {status.github_oauth && (
            <>
              <div style={{ position: 'relative', margin: '1.5rem 0' }}>
                <div
                  style={{
                    position: 'absolute',
                    inset: '0',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <div
                    style={{
                      width: '100%',
                      borderTop: '1px solid var(--border-color)',
                    }}
                  ></div>
                </div>
                <div
                  style={{
                    position: 'relative',
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <span
                    style={{
                      backgroundColor: 'var(--bg-primary)',
                      padding: '0 0.5rem',
                      color: 'var(--text-secondary)',
                      fontSize: '0.875rem',
                    }}
                  >
                    或使用以下方式继续
                  </span>
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  gap: '1rem',
                  justifyContent: 'center',
                }}
              >
                {status.github_oauth && (
                  <Button
                    variant='secondary'
                    onClick={onGitHubOAuthClicked}
                    icon={Github}
                    aria-label='GitHub 登录'
                    style={{
                      borderRadius: '50%',
                      padding: '0.75rem',
                      width: 'auto',
                    }}
                  />
                )}
              </div>
            </>
          )}
        </Card>

        {status.footer_html && (
          <div
            className='mt-8 text-center text-sm text-gray-500'
            style={{
              marginTop: '2rem',
              textAlign: 'center',
              fontSize: '0.875rem',
              color: 'var(--text-secondary)',
            }}
            dangerouslySetInnerHTML={{ __html: status.footer_html }}
          />
        )}
      </div>
    </div>
  );
};

export default LoginForm;
